// src/components/CreateSpace/Step2/components/ServicesSection.jsx

import React, { useEffect, useState } from "react";
import { 
  Checkbox, 
  NumberInput, 
  FormDropdown, 
  CustomInput, 
  ServiceSvgIcon,
  MessageBox,
} from "reusableComponents";
import { currencyToSymbol } from "helpers/currencyToSymbol";
import { SERVICE_RENTAL_TYPES } from "../../step2.constants";
import servicesStyles from "./styles.module.scss";
import typographyStyles from "../../styles/_typography.module.scss";

// Professional SVG Icons
const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.232 5.232L18.768 8.768L16.5 11.036L12.964 7.5L15.232 5.232Z" fill="currentColor"/>
    <path d="M5 16.5L11.5 10L14 12.5L7.5 19H5V16.5Z" fill="currentColor"/>
    <path d="M3 21L8.5 19.5L4.5 15.5L3 21Z" fill="currentColor"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" fill="currentColor"/>
    <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19V4Z" fill="currentColor"/>
    <path d="M10 9H12V17H10V9Z" fill="currentColor"/>
    <path d="M14 9H16V17H14V9Z" fill="currentColor"/>
  </svg>
);

const SaveIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3Z" fill="currentColor"/>
    <path d="M12 19C10.34 19 9 17.66 9 16C9 14.34 10.34 13 12 13C13.66 13 15 14.34 15 16C15 17.66 13.66 19 12 19Z" fill="white"/>
    <path d="M15 9H5V5H15V9Z" fill="white"/>
  </svg>
);

const CancelIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
  </svg>
);

const ServiceCheckbox = ({ service, isChecked, onToggle }) => (
  <Checkbox
    isSquare
    checked={isChecked}
    onChange={onToggle}
    iconType
    label={
      <div className={servicesStyles.checkboxLabelContainer}>
        <ServiceSvgIcon
          iconPath={service.image_url}
          color={isChecked ? "#72ccdf" : "#e3e3e3"}
          fillColor="white"
        />
        <span className={servicesStyles.serviceName}>{service.name}</span>
      </div>
    }
  />
);

const ActionConfirmationModal = ({ isOpen, onClose, onConfirm, action, serviceName, t }) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen && !isAnimating) return null;
  
  return (
    <div className={`${servicesStyles.modalOverlay} ${isOpen ? servicesStyles.modalOverlayVisible : ''}`}>
      <div className={`${servicesStyles.modalContent} ${isOpen ? servicesStyles.modalContentVisible : ''}`}>
        <div className={servicesStyles.modalIcon}>
          {action === 'delete' ? (
            <div className={servicesStyles.modalIconDanger}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#fee2e2" stroke="#dc2626" strokeWidth="1.5"/>
                <path d="M12 8V12M12 16H12.01" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
          ) : (
            <div className={servicesStyles.modalIconSuccess}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#e0f2fe" stroke="#72ccdf" strokeWidth="1.5"/>
                <path d="M9 12L11 14L15 10" stroke="#72ccdf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          )}
        </div>
        <h3 className={servicesStyles.modalTitle}>
          {action === 'delete' 
            ? t("provide.step2.onSiteServiceCustom.modal.deleteTitle") 
            : t("provide.step2.onSiteServiceCustom.modal.saveTitle")}
        </h3>
        <p className={servicesStyles.modalMessage}>
          {action === 'delete' 
            ? t("provide.step2.onSiteServiceCustom.modal.deleteConfirmation")
            : t("provide.step2.onSiteServiceCustom.modal.saveConfirmation")}
          <strong>"{serviceName}"</strong>?
        </p>
        <div className={servicesStyles.modalButtons}>
          <button onClick={onClose} className={servicesStyles.modalButtonCancel}>
            {action === 'delete' ? t("buttons.cancel") : t("buttons.cancel")}
          </button>
          <button onClick={onConfirm} className={action === 'delete' ? servicesStyles.modalButtonDanger : servicesStyles.modalButtonSuccess}>
            {action === 'delete' 
              ? t("provide.step2.onSiteServiceCustom.delete.button") 
              : t("provide.step2.onSiteServiceCustom.save.button")}
          </button>
        </div>
      </div>
    </div>
  );
};

const ServiceCard = ({ 
  service, 
  editingServiceId, 
  editValues, 
  onEdit, 
  onSave, 
  onCancel, 
  onDelete, 
  onEditChange, 
  isUpdating, 
  isDeleting, 
  formValues, 
  fieldErrors, 
  handlePriceChange, 
  toggleServiceSelection,
  t
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isEditing = editingServiceId === service.id;

  return (
    <div 
      className={`${servicesStyles.serviceCard} ${isHovered ? servicesStyles.serviceCardHovered : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={servicesStyles.serviceCardContent}>
        <div className={servicesStyles.serviceMainInfo}>
          <ServiceCheckbox
            service={service}
            isChecked={service.available}
            onToggle={() => toggleServiceSelection(service.id)}
          />
          {isEditing ? (
            <div className={servicesStyles.editNameWrapper}>
              <CustomInput
                value={editValues.name}
                handleChange={(e) => onEditChange('name', e.target.value)}
                placeholder={t("provide.step2.onSiteServiceCustom.editPlaceholder")}
                className={servicesStyles.editNameInput}
                autoFocus
              />
            </div>
          ) : (
            <span className={servicesStyles.serviceName}>{service.name}</span>
          )}
        </div>
        
        <div className={servicesStyles.serviceActions}>
          {isEditing ? (
            <div className={servicesStyles.editActions}>
              <button
                onClick={() => onSave(service)}
                className={`${servicesStyles.actionButton} ${servicesStyles.actionButtonSave}`}
                disabled={isUpdating}
                type="button"
                title={t("provide.step2.onSiteServiceCustom.save.title")}
              >
                <SaveIcon />
                <span>{t("provide.step2.onSiteServiceCustom.save.button")}</span>
              </button>
              <button
                onClick={onCancel}
                className={`${servicesStyles.actionButton} ${servicesStyles.actionButtonCancel}`}
                disabled={isUpdating}
                type="button"
                title={t("provide.step2.onSiteServiceCustom.cancel.title")}
              >
                <CancelIcon />
                <span>{t("provide.step2.onSiteServiceCustom.cancel.button")}</span>
              </button>
            </div>
          ) : (
            <div className={servicesStyles.actionButtons}>
              <button
                onClick={() => onEdit(service)}
                className={`${servicesStyles.actionButton} ${servicesStyles.actionButtonEdit}`}
                disabled={isUpdating || isDeleting}
                type="button"
                title={t("provide.step2.onSiteServiceCustom.edit.title")}
              >
                <EditIcon />
                <span>{t("provide.step2.onSiteServiceCustom.edit.button")}</span>
              </button>
              <button
                onClick={() => onDelete(service)}
                className={`${servicesStyles.actionButton} ${servicesStyles.actionButtonDelete}`}
                disabled={isDeleting}
                type="button"
                title={t("provide.step2.onSiteServiceCustom.delete.title")}
              >
                <DeleteIcon />
                <span>{t("provide.step2.onSiteServiceCustom.delete.button")}</span>
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className={servicesStyles.servicePriceSection}>
        {isEditing ? (
          <div className={servicesStyles.editPriceWrapper}>
            <NumberInput
              disabled={!service.available || isUpdating}
              labelAbove
              label={t("provide.step2.onSiteServiceCustom.priceLabel")}
              value={editValues.price}
              placeholder={t("provide.step2.onSiteServiceCustom.pricePlaceholder")}
              addonText={currencyToSymbol(formValues?.currency)}
              onChange={(e) => onEditChange('price', e.target.value)}
              error={fieldErrors[`service_${service.id}_price`]}
              className={servicesStyles.priceInput}
            />
          </div>
        ) : (
          <NumberInput
            disabled={!service.available || isUpdating || isDeleting}
            labelAbove
            label={t("provide.step2.onSiteServiceCustom.priceLabel")}
            value={service.price}
            placeholder={t("provide.step2.onSiteServiceCustom.pricePlaceholder")}
            addonText={currencyToSymbol(formValues?.currency)}
            onChange={(e) => handlePriceChange(e.target.value, service.id)}
            error={fieldErrors[`service_${service.id}_price`]}
            className={servicesStyles.priceInput}
          />
        )}
      </div>
    </div>
  );
};

export const CustomServicesSection = ({ 
  services, 
  customServices, 
  fieldErrors, 
  toggleServiceSelection, 
  handlePriceChange,
  handleNewServiceNameChange,
  handleNewServicePriceChange,
  handleUpdateCustomService,
  handleDeleteCustomService,
  isUpdating,
  isDeleting,
  formValues,
  t 
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [serviceToAction, setServiceToAction] = useState(null);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editValues, setEditValues] = useState({ name: '', price: '' });
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const servicesArray = Array.isArray(services) 
    ? services 
    : services && typeof services === 'object' 
      ? Object.values(services) 
      : [];

  const existingCustomServices = servicesArray
    ?.filter((service) => service?.is_custom === 1)
    ?.sort((a, b) => (a?.order || 0) - (b?.order || 0)) || [];

  const safeCustomServices = Array.isArray(customServices) ? customServices : [];

  const handleEditClick = (service) => {
    setEditingServiceId(service.id);
    setEditValues({ 
      name: service.name || '', 
      price: service.price || '' 
    });
  };

  const handleEditChange = (field, value) => {
    setEditValues(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveClick = (service) => {
    setServiceToAction({ id: service.id, name: editValues.name });
    setEditModalOpen(true);
  };

  const confirmSaveEdit = async () => {
    if (serviceToAction && handleUpdateCustomService) {
      try {
        await handleUpdateCustomService(serviceToAction.id, { 
          name: editValues.name, 
          price: parseFloat(editValues.price) 
        });
        setSuccessMessage(t("provide.step2.onSiteServiceCustom.toast.updateSuccess", { name: editValues.name }));
        setTimeout(() => setSuccessMessage(null), 3000);
        setEditModalOpen(false);
        setEditingServiceId(null);
        setServiceToAction(null);
        setEditValues({ name: '', price: '' });
      } catch (error) {
        setErrorMessage(t("provide.step2.onSiteServiceCustom.toast.updateFailed"));
        setTimeout(() => setErrorMessage(null), 3000);
      }
    }
  };

  const handleDeleteClick = (service) => {
    setServiceToAction(service);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (serviceToAction && handleDeleteCustomService) {
      try {
        await handleDeleteCustomService(serviceToAction.id);
        setSuccessMessage(t("provide.step2.onSiteServiceCustom.toast.deleteSuccess", { name: serviceToAction.name }));
        setTimeout(() => setSuccessMessage(null), 3000);
        setDeleteModalOpen(false);
        setServiceToAction(null);
      } catch (error) {
        setErrorMessage(t("provide.step2.onSiteServiceCustom.toast.deleteFailed"));
        setTimeout(() => setErrorMessage(null), 3000);
      }
    }
  };

  const cancelAction = () => {
    setDeleteModalOpen(false);
    setEditModalOpen(false);
    setEditingServiceId(null);
    setServiceToAction(null);
    setEditValues({ name: '', price: '' });
  };

  return (
    <>
      {successMessage && (
        <div className={servicesStyles.toastSuccess}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{successMessage}</span>
        </div>
      )}
      
      {errorMessage && (
        <div className={servicesStyles.toastError}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span>{errorMessage}</span>
        </div>
      )}

      <div className={servicesStyles.section}>
        <div className={servicesStyles.sectionHeader}>
          <h4 className={typographyStyles.subtitle}>{t("provide.step2.onSiteServiceCustom.title")}</h4>
          <p className={servicesStyles.sectionDescription}>
            {t("provide.step2.onSiteServiceCustom.description")}
          </p>
        </div>
        
        <div className={servicesStyles.customServicesContainer}>
          {existingCustomServices.length > 0 && (
            <div className={servicesStyles.existingServicesSection}>
              <div className={servicesStyles.existingServicesHeader}>
                <div className={servicesStyles.existingServicesIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 6V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className={servicesStyles.existingServicesTitle}>
                  {t("provide.step2.onSiteServiceCustom.yourServices")}
                </span>
                <span className={servicesStyles.existingServicesCount}>
                  {t("provide.step2.onSiteServiceCustom.serviceCount", { count: existingCustomServices.length })}
                </span>
              </div>
              
              {existingCustomServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  editingServiceId={editingServiceId}
                  editValues={editValues}
                  onEdit={handleEditClick}
                  onSave={handleSaveClick}
                  onCancel={cancelAction}
                  onDelete={handleDeleteClick}
                  onEditChange={handleEditChange}
                  isUpdating={isUpdating}
                  isDeleting={isDeleting}
                  formValues={formValues}
                  fieldErrors={fieldErrors}
                  handlePriceChange={handlePriceChange}
                  toggleServiceSelection={toggleServiceSelection}
                  t={t}
                />
              ))}
            </div>
          )}
          
          <div className={servicesStyles.addNewSection}>
            <div className={servicesStyles.addNewHeader}>
              <div className={servicesStyles.addNewIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <span className={servicesStyles.addNewTitle}>
                {t("provide.step2.onSiteServiceCustom.addNew")}
              </span>
            </div>
            
            {safeCustomServices.map((service, index) => (
              <div key={`new-${index}`} className={servicesStyles.newServiceCard}>
                <CustomInput
                  labelAbove
                  label={t("provide.step2.onSiteServiceCustom.serviceName")}
                  value={service?.name || ''}
                  handleChange={(e) => handleNewServiceNameChange(e.target.value, index)}
                  error={fieldErrors[`custom_service_${index}_name`]}
                  placeholder={t("provide.step2.onSiteServiceCustom.serviceNamePlaceholder")}
                  className={servicesStyles.newServiceNameInput}
                />
                <NumberInput
                  label={t("provide.step2.onSiteServiceCustom.priceLabel")}
                  labelAbove
                  value={service?.price || ''}
                  placeholder={t("provide.step2.onSiteServiceCustom.pricePlaceholder")}
                  addonText={currencyToSymbol(formValues?.currency)}
                  onChange={(e) => handleNewServicePriceChange(e.target.value, index)}
                  error={fieldErrors[`custom_service_${index}_price`]}
                  className={servicesStyles.newServicePriceInput}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <ActionConfirmationModal
        isOpen={deleteModalOpen}
        onClose={cancelAction}
        onConfirm={confirmDelete}
        action="delete"
        serviceName={serviceToAction?.name}
        t={t}
      />

      <ActionConfirmationModal
        isOpen={editModalOpen}
        onClose={cancelAction}
        onConfirm={confirmSaveEdit}
        action="save"
        serviceName={serviceToAction?.name}
        t={t}
      />
    </>
  );
};

// Keep your existing sections below (FreeServicesSection, ReservableServicesSection, OnsiteServicesSection, SeminarSection)
// They remain unchanged
export const FreeServicesSection = ({ services, fieldErrors, toggleServiceSelection, t }) => (
  <div className={servicesStyles.section}>
    <h4 className={typographyStyles.subtitle}>{t("provide.step2.freeService.title")}</h4>
    <MessageBox type="info" message={t("provide.step2.freeService.help")} />
    <div className={servicesStyles.servicesGrid}>
      {services
        ?.filter((service) => service.type === "free")
        ?.sort((a, b) => a.order - b.order)
        ?.map((service) => (
          <div key={service.id}>
            <ServiceCheckbox
              service={service}
              isChecked={service.available}
              onToggle={() => toggleServiceSelection(service.id)}
            />
          </div>
        ))}
    </div>
  </div>
);

export const ReservableServicesSection = ({ 
  services, 
  fieldErrors, 
  toggleServiceSelection, 
  handlePriceChange,
  handleLocationTypeChange,
  handleDescriptionChange,
  formValues,
  t 
}) => (
  <div className={servicesStyles.section}>
    <h4 className={typographyStyles.subtitle}>{t("provide.step2.reservationService.title")}</h4>
    <MessageBox type="info" message={t("provide.step2.reservationService.help")} />
    <div className={servicesStyles.reservableServices}>
      {services
        ?.filter((service) => service.type === "reservation")
        ?.sort((a, b) => a.order - b.order)
        ?.map((service) => (
          <div key={service.id} className={servicesStyles.reservableService}>
            <ServiceCheckbox
              service={service}
              isChecked={service.available}
              onToggle={() => toggleServiceSelection(service.id)}
            />
            {service.available && (
              <>
                <NumberInput
                  disabled={!service.available || service.location_type === "included"}
                  value={service.price}
                  placeholder={t("provide.step2.reservationService.price")}
                  addonText={
                    service.location
                      ? currencyToSymbol(formValues?.currency)
                      : `${currencyToSymbol(formValues?.currency)}${t("space.services.byUnit")}`
                  }
                  onChange={(e) => handlePriceChange(e.target.value, service.id)}
                  error={fieldErrors[`service_${service.id}_price`]}
                />
                {service.location ? (
                  <FormDropdown
                    disabled={!service.available}
                    placeholder={t("provide.step2.reservationService.rental")}
                    options={SERVICE_RENTAL_TYPES}
                    selected={service.location_type}
                    onSelect={(value) => handleLocationTypeChange(value, service.id)}
                    error={fieldErrors[`service_${service.id}_location_type`]}
                  />
                ) : (
                  <CustomInput
                    disabled={!service.available}
                    placeholder={t("provide.step2.reservationService.description")}
                    value={service.description}
                    handleChange={(e) => handleDescriptionChange(e.target.value, service.id)}
                    error={fieldErrors[`service_${service.id}_description`]}
                  />
                )}
              </>
            )}
          </div>
        ))}
    </div>
  </div>
);

export const OnsiteServicesSection = ({ 
  services, 
  fieldErrors, 
  toggleServiceSelection, 
  handlePriceChange,
  formValues,
  t 
}) => (
  <div className={servicesStyles.section}>
    <h4 className={typographyStyles.subtitle}>{t("provide.step2.onSiteService.title")}</h4>
    <MessageBox type="info" message={t("provide.step2.onSiteService.help")} />
    <div className={servicesStyles.onsiteServices}>
      {services
        ?.filter((service) => service.type === "onsite" && service.is_custom === 0)
        ?.sort((a, b) => a.order - b.order)
        ?.map((service) => (
          <div key={service.id} className={servicesStyles.onsiteService}>
            <ServiceCheckbox
              service={service}
              isChecked={service.available}
              onToggle={() => toggleServiceSelection(service.id)}
            />
            {service.available && (
              <NumberInput
                disabled={!service.available}
                labelAbove
                required
                label={t("provide.step2.onSiteService.price")}
                value={service.price}
                placeholder={t("provide.step2.reservationService.price")}
                addonText={currencyToSymbol(formValues?.currency)}
                onChange={(e) => handlePriceChange(e.target.value, service.id)}
                error={fieldErrors[`service_${service.id}_price`]}
              />
            )}
          </div>
        ))}
    </div>
  </div>
);

export const SeminarSection = ({ 
  seminars, 
  fieldErrors, 
  handleServiceOfferChange, 
  handleServicePriceChange,
  formValues,
  t 
}) => {
  const renderSeminarType = (type, title) => {
    const filteredSeminars = seminars
      ?.map((seminar, originalIndex) =>
        seminar?.type === type ? { ...seminar, originalIndex } : null
      )
      .filter(Boolean) ?? [];

    return (
      <React.Fragment key={type}>
        <h3 className={servicesStyles.grayTitle}>{title}</h3>
        {filteredSeminars.length > 0 ? (
          filteredSeminars.map(({ originalIndex, ...seminar }) => (
            <section key={originalIndex} className={servicesStyles.seminarService}>
              <div className={servicesStyles.seminarGroupedInputs}>
                <CustomInput
                  labelAbove
                  label={t("provide.step2.seminarService.descriptionOffer")}
                  type="text"
                  value={seminar?.offer ?? ""}
                  handleChange={(e) => handleServiceOfferChange(e, originalIndex)}
                  error={fieldErrors[`seminar_${originalIndex}_offer`]}
                />
                <NumberInput
                  step="0.01"
                  label={t("provide.step2.reservationService.price")}
                  value={seminar?.price ?? ""}
                  onChange={(e) => handleServicePriceChange(e.target.value, originalIndex)}
                  placeholder="0.00"
                  labelAbove
                  addonText={t("provide.step2.seminarService.price")}
                  error={fieldErrors[`seminar_${originalIndex}_price`]}
                />
              </div>
            </section>
          ))
        ) : (
          <p className={servicesStyles.noSeminarText}>
            {t("provide.step2.seminarService.noSeminars")}
          </p>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className={servicesStyles.section} data-section="seminar">
      <h4 className={typographyStyles.subtitle}>
        {formValues?.placeUsage?.key === "seminar" 
          ? t("provide.step2.seminarService.title")
          : t("provide.step2.seminarService.title")}
      </h4>
      
      {fieldErrors?.seminars && (
        <MessageBox 
          type="error" 
          message={fieldErrors.seminars}
          className={servicesStyles.seminarError}
        />
      )}
      
      <MessageBox 
        type="info" 
        message={formValues?.placeUsage?.key === "seminar" 
          ? t("provide.step2.seminarService.help")
          : t("provide.step2.seminarService.help")} 
      />
      
      <section>
        {[
          { type: "halfday", title: t("provide.step2.seminarService.halfdayTitle") },
          { type: "day", title: t("provide.step2.seminarService.dayTitle") },
          { type: "residential", title: t("provide.step2.seminarService.residentialTitle") },
        ].map(({ type, title }) => renderSeminarType(type, title))}
      </section>
    </div>
  );
};